/**
 * Elegir entre lo que ya usa el blog, o crearlo escribiéndolo: las categorías
 * de una entrada y su autor.
 *
 * Ni las categorías ni los autores tienen tabla propia —son lo que lleva cada
 * entrada—, así que «los que ya hay» son los de las demás entradas, con cuántas
 * los usan. Eliminar uno desde la lista se lo quita a TODAS; para quitarlo sólo
 * de ésta está la ✕ de su pastilla.
 *
 * Un mismo campo para los dos: las categorías admiten varias (`max`) y el autor
 * una. Con el tope alcanzado el buscador se retira y queda la pastilla.
 */
import { matchesSearch } from '@webbuilder/shared-types'
import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react'
import { IconCheck, IconPencil, IconPlus, IconTag, IconTrash, IconUser, IconX } from '@tabler/icons-react'
import { BlogAuthorsDialog } from './BlogAuthorsDialog'
import { BLOG_LIMITS, blogAuthorNames, blogAuthorValue } from '@webbuilder/schemas'
import { ConfirmDialog } from '../ConfirmDialog'
import { useToast } from '../../Toast'
import { useI18n } from '../../../lib/i18n'
import { removeBlogAuthor, removeBlogCategory } from '../../../lib/api'
import { errorMessage } from './offerShared'

export type KnownOption = { name: string; count: number }

/** Los textos de cada campo: claves enteras, para que se encuentren al buscarlas. */
type PickerTexts = {
  label: string
  placeholder: string
  create: string
  empty: string
  allChosen: string
  limit: string
  remove: string
  deleteAria: string
  deleteTitle: string
  deleteBody: string
  deleteBodyOne: string
  deleteConfirm: string
  deleted: string
  deletedOne: string
  /** Cuántos lo usan, junto a cada opción. */
  count: string
  countOne: string
}

const same = (a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: 'accent' }) === 0

/**
 * El campo en sí, para cualquier lista de etiquetas sin tabla propia: las del
 * blog y las de los contactos. Sin `icon` no pinta su título —la ficha del
 * contacto ya lo lleva en su fila—.
 */
export function PickerField({ id, icon, texts, max, maxLength, value, known, onChange, removeEverywhere, onRemovedEverywhere, onEdit, editLabel }: {
  id: string
  icon?: ReactNode
  texts: PickerTexts
  max: number
  maxLength: number
  value: string[]
  /** Los del resto del blog, con cuántas entradas los llevan. */
  known: KnownOption[]
  onChange: (next: string[]) => void
  /** Devuelve a cuántos se lo quitó. */
  removeEverywhere: (name: string) => Promise<{ count: number }>
  /** Ya se quitó de todas las entradas guardadas: la ficha lo quita también de lo suyo. */
  onRemovedEverywhere: (name: string) => void
  /** Editar una opción (el perfil de un autor): un lápiz junto a la papelera. */
  onEdit?: ((name: string) => void) | undefined
  /** El texto del lápiz, ya traducido con el nombre. */
  editLabel?: ((name: string) => string) | undefined
}) {
  const { t } = useI18n()
  const { showToast } = useToast()
  const [input, setInput] = useState('')
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const [removing, setRemoving] = useState<KnownOption | null>(null)
  /* Los eliminados desaparecen de la lista al instante, sin esperar a que el
     listado del blog vuelva a cargarse. */
  const [gone, setGone] = useState<string[]>([])
  const boxRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const full = value.length >= max
  const needle = input.trim()
  const options = useMemo(() => {
    const lower = needle.toLocaleLowerCase()
    return known.filter((option) => !gone.includes(option.name)
      && !value.some((chosen) => same(chosen, option.name))
      && matchesSearch(option.name, lower))
  }, [known, gone, value, needle])
  /* Crear sólo si no existe ya con ese nombre —en esta entrada o en otra—: dos
     «Recetas» que sólo se distinguen en una mayúscula son un error, no dos. */
  const canCreate = Boolean(needle)
    && !value.some((chosen) => same(chosen, needle))
    && !known.some((option) => !gone.includes(option.name) && same(option.name, needle))
  const rows = canCreate ? options.length + 1 : options.length

  useEffect(() => { setActive(0) }, [needle])

  useEffect(() => {
    if (!open) return
    const onDown = (event: PointerEvent) => {
      if (!boxRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('pointerdown', onDown)
    return () => document.removeEventListener('pointerdown', onDown)
  }, [open])

  const add = (name: string) => {
    const clean = name.trim().slice(0, maxLength)
    if (!clean || full || value.some((chosen) => same(chosen, clean))) return
    onChange([...value, clean])
    setInput('')
    /* Con uno solo permitido, elegirlo cierra la lista: no queda nada más que
       elegir y el buscador se retira. */
    if (value.length + 1 >= max) setOpen(false)
    else inputRef.current?.focus()
  }

  const pick = (index: number) => {
    if (canCreate && index === 0) { add(needle); return }
    const option = options[canCreate ? index - 1 : index]
    if (option) add(option.name)
  }

  const instanceId = useId()
  const listId = `${id}-${instanceId}-list`

  return (
    <div className="dash-blog-field dash-blog-categories" ref={boxRef}>
      {icon ? <span className="dash-blog-panel-label">{icon}{t(texts.label)}</span> : null}
      <div className="dash-blog-chips" onClick={() => inputRef.current?.focus()}>
        {value.map((chosen) => (
          <span key={chosen} className="dash-blog-chip">
            {/* Con perfil editable (los autores), el nombre abre su ficha. */}
            {onEdit ? (
              /* Un `span` y no otro botón: el único botón de la pastilla sigue siendo la ✕. */
              <span
                role="button"
                tabIndex={0}
                style={{ cursor: 'pointer' }}
                title={editLabel?.(chosen) ?? chosen}
                onClick={(event) => { event.stopPropagation(); onEdit(chosen) }}
                onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); event.stopPropagation(); onEdit(chosen) } }}
              >
                {chosen}
              </span>
            ) : chosen}
            <button
              type="button"
              aria-label={t(texts.remove, { name: chosen })}
              onClick={(event) => {
                event.stopPropagation()
                onChange(value.filter((item) => item !== chosen))
                /* Quitado el único, se vuelve a buscar sin otro clic. */
                window.setTimeout(() => inputRef.current?.focus(), 0)
              }}
            >
              <IconX size={12} />
            </button>
          </span>
        ))}
        {full ? null : (
          <input
            ref={inputRef}
            className="dash-blog-bare"
            value={input}
            maxLength={maxLength}
            placeholder={value.length ? '' : t(texts.placeholder)}
            aria-label={t(texts.label)}
            role="combobox"
            aria-expanded={open}
            aria-controls={listId}
            aria-autocomplete="list"
            onFocus={() => setOpen(true)}
            onChange={(event) => { setInput(event.target.value.replace(/,/g, '')); setOpen(true) }}
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown' && rows) { event.preventDefault(); setOpen(true); setActive((index) => (index + 1) % rows) }
              else if (event.key === 'ArrowUp' && rows) { event.preventDefault(); setActive((index) => (index - 1 + rows) % rows) }
              else if (event.key === 'Enter' || (event.key === ',' && max > 1)) {
                event.preventDefault()
                if (open && rows) pick(active)
                else if (needle) add(needle)
              } else if (event.key === 'Escape') setOpen(false)
              else if (event.key === 'Backspace' && !input && value.length) onChange(value.slice(0, -1))
            }}
          />
        )}
      </div>
      {full && max > 1 ? <small>{t(texts.limit, { max })}</small> : null}

      {open && !full ? (
        <div className="dash-blog-category-menu" id={listId} role="listbox" onMouseDown={(event) => event.preventDefault()}>
          {canCreate ? (
            <button
              type="button"
              role="option"
              aria-selected={active === 0}
              className={`dash-blog-category-option is-create${active === 0 ? ' is-active' : ''}`}
              onMouseEnter={() => setActive(0)}
              onClick={() => pick(0)}
            >
              <IconPlus size={14} aria-hidden="true" />
              <span>{t(texts.create, { name: needle })}</span>
            </button>
          ) : null}
          {options.map((option, at) => {
            const index = canCreate ? at + 1 : at
            return (
              <div
                key={option.name}
                role="option"
                aria-selected={active === index}
                className={`dash-blog-category-option${active === index ? ' is-active' : ''}`}
                onMouseEnter={() => setActive(index)}
                onClick={() => pick(index)}
              >
                <span className="dash-blog-category-name">{option.name}</span>
                <small>{t(option.count === 1 ? texts.countOne : texts.count, { count: option.count })}</small>
                {onEdit ? (
                  <button
                    type="button"
                    className="dash-blog-category-delete"
                    aria-label={editLabel?.(option.name) ?? option.name}
                    title={editLabel?.(option.name) ?? option.name}
                    onClick={(event) => { event.stopPropagation(); setOpen(false); onEdit(option.name) }}
                  >
                    <IconPencil size={13} />
                  </button>
                ) : null}
                <button
                  type="button"
                  className="dash-blog-category-delete"
                  aria-label={t(texts.deleteAria, { name: option.name })}
                  title={t(texts.deleteAria, { name: option.name })}
                  onClick={(event) => { event.stopPropagation(); setOpen(false); setRemoving(option) }}
                >
                  <IconTrash size={13} />
                </button>
              </div>
            )
          })}
          {rows === 0 ? (
            <p className="dash-blog-category-empty">
              {known.length - gone.length > 0 && !needle
                ? <><IconCheck size={13} aria-hidden="true" /> {t(texts.allChosen)}</>
                : t(texts.empty)}
            </p>
          ) : null}
        </div>
      ) : null}

      {removing ? (
        <ConfirmDialog
          title={t(texts.deleteTitle, { name: removing.name })}
          message={t(removing.count === 1 ? texts.deleteBodyOne : texts.deleteBody, { count: removing.count })}
          confirmLabel={t(texts.deleteConfirm)}
          tone="danger"
          onCancel={() => setRemoving(null)}
          onConfirm={async () => {
            try {
              const result = await removeEverywhere(removing.name)
              setGone((current) => [...current, removing.name])
              onRemovedEverywhere(removing.name)
              showToast(t(result.count === 1 ? texts.deletedOne : texts.deleted, { count: result.count }))
              setRemoving(null)
            } catch (error) {
              showToast(errorMessage(error), 'error')
              throw error
            }
          }}
        />
      ) : null}
    </div>
  )
}

const CATEGORY_TEXTS: PickerTexts = {
  label: 'blog.field.categories',
  placeholder: 'blog.categories.placeholder',
  create: 'blog.categories.create',
  empty: 'blog.categories.empty',
  allChosen: 'blog.categories.allChosen',
  limit: 'blog.categories.limit',
  remove: 'blog.field.removeCategory',
  deleteAria: 'blog.categories.deleteAria',
  deleteTitle: 'blog.categories.deleteTitle',
  deleteBody: 'blog.categories.deleteBody',
  deleteBodyOne: 'blog.categories.deleteBodyOne',
  deleteConfirm: 'blog.categories.deleteConfirm',
  deleted: 'blog.categories.deleted',
  deletedOne: 'blog.categories.deletedOne',
  count: 'blog.categories.count',
  countOne: 'blog.categories.countOne',
}

const AUTHOR_TEXTS: PickerTexts = {
  label: 'blog.field.author',
  placeholder: 'blog.authors.placeholder',
  create: 'blog.authors.create',
  empty: 'blog.authors.empty',
  allChosen: 'blog.authors.allChosen',
  limit: 'blog.authors.limit',
  remove: 'blog.authors.remove',
  deleteAria: 'blog.authors.deleteAria',
  deleteTitle: 'blog.authors.deleteTitle',
  deleteBody: 'blog.authors.deleteBody',
  deleteBodyOne: 'blog.authors.deleteBodyOne',
  deleteConfirm: 'blog.authors.deleteConfirm',
  deleted: 'blog.authors.deleted',
  deletedOne: 'blog.authors.deletedOne',
  count: 'blog.categories.count',
  countOne: 'blog.categories.countOne',
}

type FieldProps<T> = {
  hideLabel?: boolean
  projectId: string
  token: string
  value: T
  known: KnownOption[]
  onChange: (next: T) => void
  onRemovedEverywhere: (name: string) => void
}

/** Las categorías de la entrada: varias, hasta el tope del blog. */
export function BlogCategoryField({ projectId, token, value, known, onChange, onRemovedEverywhere, hideLabel = false }: FieldProps<string[]>) {
  return (
    <PickerField
      id="blog-category"
      icon={hideLabel ? undefined : <IconTag size={14} aria-hidden="true" />}
      texts={CATEGORY_TEXTS}
      max={BLOG_LIMITS.categories}
      maxLength={BLOG_LIMITS.category}
      value={value}
      known={known}
      onChange={onChange}
      removeEverywhere={(name) => removeBlogCategory(projectId, name, token).then((result) => ({ count: result.posts }))}
      onRemovedEverywhere={onRemovedEverywhere}
    />
  )
}

/** Las firmas de la entrada: cada autor se elige y se quita por separado. */
export function BlogAuthorField({ projectId, token, value, known, onChange, onRemovedEverywhere, hideLabel = false }: FieldProps<string>) {
  const { t } = useI18n()
  /* El autor cuyo perfil (foto, biografía, enlaces) se está editando desde la lista. */
  const [editing, setEditing] = useState<string | null>(null)
  return (
    <>
    {editing ? <BlogAuthorsDialog projectId={projectId} token={token} initialName={editing} onClose={() => setEditing(null)} /> : null}
    <PickerField
      id="blog-author"
      icon={hideLabel ? undefined : <IconUser size={14} aria-hidden="true" />}
      texts={AUTHOR_TEXTS}
      max={BLOG_LIMITS.authors}
      maxLength={BLOG_LIMITS.author}
      value={blogAuthorNames(value)}
      known={known}
      onChange={(next) => onChange(blogAuthorValue(next))}
      removeEverywhere={(name) => removeBlogAuthor(projectId, name, token).then((result) => ({ count: result.posts }))}
      onRemovedEverywhere={onRemovedEverywhere}
      onEdit={setEditing}
      editLabel={(name) => t('blog.authors.editNamed', { name })}
    />
    </>
  )
}

/** La firma de la hoja edita el mismo autor que el panel lateral. */
export function BlogBylineAuthor(props: FieldProps<string>) {
  const { t } = useI18n()
  const [open, setOpen] = useState(false)
  const boxRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const popupId = useId()
  const name = props.value.trim()

  useEffect(() => {
    if (!open) return
    boxRef.current?.querySelector<HTMLElement>('.dash-blog-author-popup input, .dash-blog-author-popup button')?.focus()
    const onDown = (event: PointerEvent) => {
      if (!boxRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('pointerdown', onDown)
    return () => document.removeEventListener('pointerdown', onDown)
  }, [open])

  return (
    <div className="dash-blog-byline-author" ref={boxRef} onKeyDown={(event) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        setOpen(false)
        triggerRef.current?.focus()
      }
    }}>
      <button
        ref={triggerRef}
        type="button"
        className="dash-blog-author-trigger"
        aria-expanded={open}
        aria-controls={open ? popupId : undefined}
        onClick={() => setOpen((current) => !current)}
      >
        <span className="dash-blog-byline-avatar" aria-hidden="true">
          {name ? name[0]!.toUpperCase() : <IconUser size={14} />}
        </span>
        <span>{name || t('blog.editor.noAuthor')}</span>
      </button>
      {open ? (
        <div className="dash-blog-author-popup" id={popupId}>
          <BlogAuthorField {...props} onChange={(value) => {
            props.onChange(value)
            if (value) {
              setOpen(false)
              triggerRef.current?.focus()
            }
          }} />
        </div>
      ) : null}
    </div>
  )
}
